import './index.css';
import './styles/theme.scss';

import * as Sentry from '@sentry/react';
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';

import App from './App';
import { getApiError, isTenantError, TENANT_ERROR_MESSAGE, TENANT_ERROR_TITLE } from './libs/error/apiError';
import { AUTH_ERROR, DB_VALIDATION_ERROR, INVALID_SIGNATURE, REQ_VALIDATION_ERROR } from './libs/error/const';
import { persistor, store } from './store/redux/store';
import { TENANT_KEY } from './utils/tenant';
import { notification, NotificationKinds, Notifications } from './widgets/Notifications/notifications';

const env = process.env.NODE_ENV || 'local';
if (env === 'development' || env === 'production') {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    tracesSampleRate: 0,
    integrations: [],
    environment: env
  });

  // Both companies build from this branch and report into the same Sentry project, so
  // without this an event cannot be attributed to either. Khufu tags its own events the
  // same way from resolveTenant, which keeps the two sides of a failure lined up.
  Sentry.setTag('tenant', TENANT_KEY);
}

export const queryCache = new QueryCache({
  onError: (error, query) => {
    // eslint-disable-next-line no-console
    console.log('error - ', error);
    Sentry.captureException(error);

    // Reported whether or not the query has previous data: a misconfigured build fails
    // its very first request, so there is never anything cached to fall back on.
    if (isTenantError(getApiError(error))) {
      notification({
        variant: NotificationKinds.ERROR,
        title: TENANT_ERROR_TITLE,
        subtitle: TENANT_ERROR_MESSAGE
      });
      return;
    }

    if (query.state.data !== undefined) {
      notification({
        variant: NotificationKinds.ERROR,
        title: `SOMETHING WENT WRONG`,
        subtitle: error?.message ?? ''
      });
      return;
    }
  }
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false
    },
    mutations: {
      onError: (e: unknown) => {
        const error = getApiError(e);

        if (isTenantError(error)) {
          notification({
            variant: NotificationKinds.ERROR,
            title: TENANT_ERROR_TITLE,
            subtitle: TENANT_ERROR_MESSAGE
          });
          return;
        }
        if (error.code === DB_VALIDATION_ERROR || error.code === REQ_VALIDATION_ERROR) {
          notification({
            variant: NotificationKinds.ERROR,
            title: `VALIDATION ERROR`,
            subtitle: error.message ?? ''
          });
          return;
        }
        if (error.code === INVALID_SIGNATURE || error.code === AUTH_ERROR) {
          notification({
            variant: NotificationKinds.ERROR,
            title: `SOMETHING WENT WRONG`,
            subtitle: error.message ?? ''
          });
          return;
        }
        if ((error.status ?? 0) >= 500 || !!error.code) {
          notification({
            variant: NotificationKinds.ERROR,
            title: `SOMETHING WENT WRONG`,
            subtitle: error.message ?? ''
          });
          return;
        }
        return;
      },
      retry: false
    }
  },
  queryCache
});

// const queryClient = new QueryClient();

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
root.render(
  <Provider store={store}>
    <PersistGate loading={null} persistor={persistor}>
      <QueryClientProvider client={queryClient}>
        <StrictMode>
          <Notifications />
          <App />
        </StrictMode>
      </QueryClientProvider>
    </PersistGate>
  </Provider>
);
